import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  ListGroup,
  ListGroupItem,
  Row,
  Spinner,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import DismissiblePageTip from "../../../Components/Common/DismissiblePageTip";
import PageHelpLink from "../../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../../constants/helpRouteMap";
import {
  apiErrorMessage,
  connectShopify,
  disconnectShopify,
  getShopifyIntegration,
  syncShopifyContacts,
  TenantIntegration,
  testShopifyConnection,
  updateShopify,
} from "../../../helpers/integrations_helper";
import {
  formatInTenantTimezone,
  readDisplayTimezone,
} from "../../../helpers/tenantTimezone";

type FormValues = {
  shop: string;
  access_token: string;
  api_secret: string;
};

function checklistKey(publicKey: string, part: string): string {
  return `brenvio:shopify-checklist:${publicKey}:${part}`;
}

/**
 * Conectar / gerenciar Shopify — Custom App + checklist (DOC 11 § 6.2).
 * Por quê: passos numerados evitam Carla perder o token/HMAC no Admin Shopify.
 */
const ShopifyIntegrationPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const locale = i18n.language || "pt-BR";
  const tz = readDisplayTimezone();

  document.title = t("integrations.shopify.documentTitle");
  const helpHref = helpUrlForRoute("/integrations/shopify", locale);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [row, setRow] = useState<TenantIntegration | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testOk, setTestOk] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getShopifyIntegration();
      if (res?.success) {
        setRow(res.data);
        const pk = res.data.public_key;
        setUrlCopied(localStorage.getItem(checklistKey(pk, "url")) === "1");
        setTestOk(localStorage.getItem(checklistKey(pk, "test")) === "1");
      } else {
        setRow(null);
      }
    } catch (e) {
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status === 404) {
        setRow(null);
      } else {
        setError(apiErrorMessage(e, t("integrations.error.load")));
      }
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const markCheck = (part: string) => {
    if (!row?.public_key) return;
    localStorage.setItem(checklistKey(row.public_key, part), "1");
    if (part === "url") setUrlCopied(true);
    if (part === "test") setTestOk(true);
  };

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      shop: row?.account_name || "",
      access_token: "",
      api_secret: "",
    },
    validationSchema: Yup.object({
      shop: Yup.string().required(t("integrations.shopify.shopRequired")),
      access_token: Yup.string().when([], {
        is: () => !row,
        then: (s) => s.required(t("integrations.shopify.tokenRequired")),
        otherwise: (s) => s,
      }),
      api_secret: Yup.string().when([], {
        is: () => !row,
        then: (s) => s.required(t("integrations.shopify.secretRequired")),
        otherwise: (s) => s,
      }),
    }),
    onSubmit: async (values) => {
      setSaving(true);
      try {
        if (!row) {
          const res = await connectShopify(values);
          if (res?.success) {
            setRow(res.data);
            toast.success(t("integrations.shopify.toastConnected"));
          }
        } else {
          const payload: Record<string, string> = {};
          if (values.shop) payload.shop = values.shop;
          if (values.access_token) payload.access_token = values.access_token;
          if (values.api_secret) payload.api_secret = values.api_secret;
          const res = await updateShopify(payload);
          if (res?.success) {
            setRow(res.data);
            formik.setFieldValue("access_token", "");
            formik.setFieldValue("api_secret", "");
            toast.success(t("integrations.shopify.toastUpdated"));
          }
        }
      } catch (e) {
        toast.error(apiErrorMessage(e, t("integrations.error.save")));
      } finally {
        setSaving(false);
      }
    },
  });

  const onTest = async () => {
    setTesting(true);
    try {
      const res = await testShopifyConnection();
      if (res?.success) {
        markCheck("test");
        toast.success(res.data?.message || t("integrations.shopify.toastTestOk"));
      }
    } catch (e) {
      toast.error(apiErrorMessage(e, t("integrations.error.test")));
    } finally {
      setTesting(false);
    }
  };

  const onSync = async () => {
    setSyncing(true);
    try {
      await syncShopifyContacts();
      toast.success(t("integrations.shopify.toastSyncQueued"));
      setTimeout(() => void load(), 1500);
    } catch (e) {
      toast.error(apiErrorMessage(e, t("integrations.error.sync")));
    } finally {
      setSyncing(false);
    }
  };

  const onDisconnect = async () => {
    if (!window.confirm(t("integrations.shopify.disconnectConfirm"))) return;
    try {
      await disconnectShopify();
      toast.success(t("integrations.shopify.toastDisconnected"));
      navigate("/integrations");
    } catch (e) {
      toast.error(apiErrorMessage(e, t("integrations.error.disconnect")));
    }
  };

  const copy = async (text: string, okKey: string, checkPart?: string) => {
    try {
      await navigator.clipboard.writeText(text);
      if (checkPart) markCheck(checkPart);
      toast.success(t(okKey));
    } catch {
      toast.error(t("integrations.error.copy"));
    }
  };

  const checklistItems = row
    ? [
        { id: "creds", done: true, label: t("integrations.shopify.check.creds") },
        { id: "test", done: testOk, label: t("integrations.shopify.check.test") },
        { id: "url", done: urlCopied, label: t("integrations.shopify.check.url") },
      ]
    : [];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("integrations.shopify.title")}
            pageTitle={t("menu.integrations")}
          />
          <div className="d-flex justify-content-between align-items-start mb-3 gap-2 flex-wrap">
            <DismissiblePageTip tipId="integrations-shopify">
              {t("integrations.shopify.tip")}
            </DismissiblePageTip>
            <PageHelpLink href={helpHref} label={t("integrations.help")} />
          </div>

          {error && (
            <Alert color="danger" className="mb-3">
              {error}
            </Alert>
          )}

          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <Row>
              <Col lg={7}>
                {!row && (
                  <Card className="mb-3">
                    <CardHeader>
                      <h5 className="card-title mb-0">
                        {t("integrations.shopify.wizardTitle")}
                      </h5>
                    </CardHeader>
                    <CardBody>
                      <ListGroup flush>
                        <ListGroupItem>{t("integrations.shopify.wizard.step1")}</ListGroupItem>
                        <ListGroupItem>{t("integrations.shopify.wizard.step2")}</ListGroupItem>
                        <ListGroupItem>{t("integrations.shopify.wizard.step3")}</ListGroupItem>
                        <ListGroupItem>{t("integrations.shopify.wizard.step4")}</ListGroupItem>
                      </ListGroup>
                    </CardBody>
                  </Card>
                )}
                <Card>
                  <CardHeader className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">
                      {row
                        ? t("integrations.shopify.manageTitle")
                        : t("integrations.shopify.connectTitle")}
                    </h5>
                    {row && (
                      <Badge color={row.enabled ? "success" : "secondary"}>
                        {row.enabled
                          ? t("integrations.connected")
                          : t("integrations.disabled")}
                      </Badge>
                    )}
                  </CardHeader>
                  <CardBody>
                    <Form onSubmit={formik.handleSubmit}>
                      <div className="mb-3">
                        <Label>{t("integrations.shopify.shop")}</Label>
                        <Input
                          name="shop"
                          value={formik.values.shop}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={!!(formik.touched.shop && formik.errors.shop)}
                          placeholder="minha-loja.myshopify.com"
                        />
                        <FormFeedback>{formik.errors.shop}</FormFeedback>
                      </div>
                      <div className="mb-3">
                        <Label>{t("integrations.shopify.accessToken")}</Label>
                        <Input
                          type="password"
                          name="access_token"
                          autoComplete="off"
                          value={formik.values.access_token}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            !!(formik.touched.access_token && formik.errors.access_token)
                          }
                          placeholder={
                            row?.app_key_masked
                              ? t("integrations.shopify.tokenKeep")
                              : undefined
                          }
                        />
                        <FormFeedback>{formik.errors.access_token}</FormFeedback>
                      </div>
                      <div className="mb-3">
                        <Label>{t("integrations.shopify.apiSecret")}</Label>
                        <Input
                          type="password"
                          name="api_secret"
                          autoComplete="off"
                          value={formik.values.api_secret}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          invalid={
                            !!(formik.touched.api_secret && formik.errors.api_secret)
                          }
                          placeholder={
                            row ? t("integrations.shopify.secretKeep") : undefined
                          }
                        />
                        <FormFeedback>{formik.errors.api_secret}</FormFeedback>
                        <p className="text-muted small mb-0 mt-1">
                          {t("integrations.shopify.apiSecretHelp")}
                        </p>
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        <Button color="primary" type="submit" disabled={saving}>
                          {saving && <Spinner size="sm" className="me-1" />}
                          {row
                            ? t("integrations.shopify.save")
                            : t("integrations.connect")}
                        </Button>
                        {row && (
                          <>
                            <Button
                              color="secondary"
                              outline
                              type="button"
                              disabled={testing}
                              onClick={() => void onTest()}
                            >
                              {testing && <Spinner size="sm" className="me-1" />}
                              {t("integrations.shopify.test")}
                            </Button>
                            <Button
                              color="success"
                              outline
                              type="button"
                              disabled={syncing}
                              onClick={() => void onSync()}
                            >
                              {syncing && <Spinner size="sm" className="me-1" />}
                              {t("integrations.shopify.sync")}
                            </Button>
                            <Button
                              color="danger"
                              outline
                              type="button"
                              onClick={() => void onDisconnect()}
                            >
                              {t("integrations.shopify.disconnect")}
                            </Button>
                          </>
                        )}
                      </div>
                    </Form>
                  </CardBody>
                </Card>
              </Col>
              <Col lg={5}>
                {row && (
                  <>
                    <Card className="mb-3">
                      <CardHeader>
                        <h5 className="card-title mb-0">
                          {t("integrations.shopify.checklistTitle")}
                        </h5>
                      </CardHeader>
                      <CardBody>
                        <p className="text-muted small">
                          {t("integrations.shopify.checklistTip")}
                        </p>
                        <ListGroup flush>
                          {checklistItems.map((item) => (
                            <ListGroupItem
                              key={item.id}
                              className="px-0 d-flex align-items-center gap-2"
                            >
                              <i
                                className={
                                  item.done
                                    ? "ri-checkbox-circle-fill text-success"
                                    : "ri-checkbox-blank-circle-line text-muted"
                                }
                              />
                              <span>{item.label}</span>
                            </ListGroupItem>
                          ))}
                        </ListGroup>
                      </CardBody>
                    </Card>
                    <Card className="mb-3">
                      <CardHeader>
                        <h5 className="card-title mb-0">
                          {t("integrations.shopify.webhookTitle")}
                        </h5>
                      </CardHeader>
                      <CardBody>
                        <p className="text-muted small">
                          {t("integrations.shopify.webhookHelp")}
                        </p>
                        <Label>{t("integrations.shopify.webhookUrl")}</Label>
                        <div className="d-flex gap-2 mb-2">
                          <Input readOnly value={row.webhook_url} />
                          <Button
                            color="light"
                            onClick={() =>
                              void copy(
                                row.webhook_url,
                                "integrations.shopify.toastCopied",
                                "url"
                              )
                            }
                          >
                            {t("integrations.shopify.copy")}
                          </Button>
                        </div>
                        <p className="small text-muted mb-0">
                          {t("integrations.shopify.hmacNote")}
                        </p>
                      </CardBody>
                    </Card>
                    <Card className="mb-3">
                      <CardBody>
                        <p className="mb-1">
                          <strong>{t("integrations.shopify.customersSynced")}:</strong>{" "}
                          {row.customer_count ?? row.vtex_customer_count ?? 0}
                        </p>
                        <p className="mb-1 small text-muted">
                          {t("integrations.shopify.lastSync")}:{" "}
                          {row.last_synced_at
                            ? formatInTenantTimezone(row.last_synced_at, tz, locale)
                            : "—"}
                        </p>
                        {!row.automation_enabled && (
                          <Alert color="warning" className="mb-0 mt-2">
                            {t("integrations.shopify.automationOff")}{" "}
                            <Link to="/automations/abandoned-cart">
                              {t("integrations.shopify.openAutomation")}
                            </Link>
                          </Alert>
                        )}
                      </CardBody>
                    </Card>
                  </>
                )}
                <Button color="link" tag={Link} to="/integrations" className="px-0">
                  {t("integrations.backToHub")}
                </Button>
              </Col>
            </Row>
          )}
        </Container>
      </div>
      <ToastContainer />
    </React.Fragment>
  );
};

export default ShopifyIntegrationPage;
